import { useState, useCallback, useEffect } from 'react';
import { CustomObjectRecord, CreateCustomObjectRecordInput } from './types';

const STORAGE_KEY = 'helios_custom_object_records';
const RECORDS_EVENT = 'helios-custom-object-records-updated';

const INITIAL_DEMO_RECORDS: CustomObjectRecord[] = [
  {
    id: 'rec-1',
    object_slug: 'partner_tier',
    name: 'Platinum Elite Partner',
    status: 'active',
    company_name: 'Acme Corporation',
    contact_name: 'Sarah Connor',
    data: {
      rebate_percentage: '15%',
      min_annual_quota: '$500,000',
      dedicated_rep: 'Sarah Connor',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'rec-2',
    object_slug: 'partner_tier',
    name: 'Gold Certified Partner',
    status: 'active',
    company_name: 'Globex Industries',
    contact_name: 'John Doe',
    data: {
      rebate_percentage: '10%',
      min_annual_quota: '$250,000',
      dedicated_rep: 'John Doe',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'rec-3',
    object_slug: 'partner_tier',
    name: 'Silver Registered Partner',
    status: 'active',
    company_name: 'Initech',
    contact_name: 'Alice Chen',
    data: {
      rebate_percentage: '5%',
      min_annual_quota: '$100,000',
      dedicated_rep: 'Alice Chen',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'rec-4',
    object_slug: 'it_asset',
    name: 'MacBook Pro 16" M3 Max',
    status: 'active',
    company_name: 'Acme Corporation',
    contact_name: 'Alice Chen',
    data: {
      serial_number: 'C02G80XZMD6T',
      assigned_to: 'Alice Chen',
      purchase_date: '2025-01-15',
    },
    created_at: new Date().toISOString(),
  },
  {
    id: 'rec-5',
    object_slug: 'it_asset',
    name: 'Dell UltraSharp 32" 4K Monitor',
    status: 'active',
    company_name: 'Initech',
    contact_name: 'John Doe',
    data: {
      serial_number: 'CN-0P820R-74261',
      assigned_to: 'John Doe',
      purchase_date: '2025-02-01',
    },
    created_at: new Date().toISOString(),
  },
];

function getStoredRecords(): CustomObjectRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }
  return INITIAL_DEMO_RECORDS;
}

export function useCustomObjectRecords(objectSlug?: string) {
  const [allRecords, setAllRecords] = useState<CustomObjectRecord[]>(getStoredRecords);

  useEffect(() => {
    const handleUpdate = () => {
      setAllRecords(getStoredRecords());
    };
    window.addEventListener(RECORDS_EVENT, handleUpdate);
    window.addEventListener('storage', handleUpdate);
    return () => {
      window.removeEventListener(RECORDS_EVENT, handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, []);

  const saveRecords = (records: CustomObjectRecord[]) => {
    setAllRecords(records);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
      window.dispatchEvent(new CustomEvent(RECORDS_EVENT));
    } catch {
      // ignore
    }
  };

  const records = objectSlug
    ? allRecords.filter((r) => r.object_slug === objectSlug)
    : allRecords;

  const createRecord = useCallback(
    (input: CreateCustomObjectRecordInput) => {
      const newRec: CustomObjectRecord = {
        ...input,
        id: `rec-${Date.now()}`,
        status: input.status || 'active',
        created_at: new Date().toISOString(),
      };
      saveRecords([newRec, ...allRecords]);
      return newRec;
    },
    [allRecords]
  );

  const updateRecord = useCallback(
    (id: string, input: Partial<CustomObjectRecord>) => {
      const next = allRecords.map((r) =>
        r.id === id ? { ...r, ...input, updated_at: new Date().toISOString() } : r
      );
      saveRecords(next);
    },
    [allRecords]
  );

  const deleteRecord = useCallback(
    (id: string) => {
      const next = allRecords.filter((r) => r.id !== id);
      saveRecords(next);
    },
    [allRecords]
  );

  const attachRecords = useCallback(
    (
      recordIds: string[],
      entity: { type: 'company' | 'contact'; id: string; name: string }
    ) => {
      const next = allRecords.map((r) => {
        if (entity.type === 'company') {
          if (recordIds.includes(r.id)) {
            return { ...r, company_id: entity.id, company_name: entity.name };
          }
          if (
            r.company_id === entity.id ||
            (r.company_name && r.company_name.toLowerCase() === entity.name.toLowerCase())
          ) {
            return { ...r, company_id: undefined, company_name: undefined };
          }
        } else if (entity.type === 'contact') {
          if (recordIds.includes(r.id)) {
            return { ...r, contact_id: entity.id, contact_name: entity.name };
          }
          if (
            r.contact_id === entity.id ||
            (r.contact_name && r.contact_name.toLowerCase() === entity.name.toLowerCase())
          ) {
            return { ...r, contact_id: undefined, contact_name: undefined };
          }
        }
        return r;
      });
      saveRecords(next);
    },
    [allRecords]
  );

  const detachRecord = useCallback(
    (recordId: string, entityType: 'company' | 'contact') => {
      const next = allRecords.map((r) => {
        if (r.id !== recordId) return r;
        if (entityType === 'company') {
          return { ...r, company_id: undefined, company_name: undefined };
        }
        return { ...r, contact_id: undefined, contact_name: undefined };
      });
      saveRecords(next);
    },
    [allRecords]
  );

  return {
    records,
    allRecords,
    createRecord,
    updateRecord,
    deleteRecord,
    attachRecords,
    detachRecord,
  };
}

export default useCustomObjectRecords;
